import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Manifest, CourierIntegration } from '../models';
import { Button } from '@shared/components/ui/button';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faFilePdf, faUpload } from '@fortawesome/pro-light-svg-icons';
import { getManifestStatusConfig } from '../config/manifestStatusConfig';

interface UseManifestsAgGridColumnsProps {
  selectedColumns?: string[];
  courierIntegrations?: CourierIntegration[];
  onUploadPdf?: (manifest: Manifest) => void;
  onDownloadExcel?: (manifest: Manifest) => void;
  onViewPdf?: (manifest: Manifest) => void;
}

const formatDateTime = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return format(parsed, 'dd/MM/yyyy HH:mm');
};

export const useManifestsAgGridColumns = ({
  selectedColumns = [],
  courierIntegrations = [],
  onUploadPdf,
  onDownloadExcel,
  onViewPdf,
}: UseManifestsAgGridColumnsProps) => {
  const { t } = useTranslation('manifestsModule');

  const courierMap = useMemo(() => {
    const map = new Map<number, string>();
    courierIntegrations.forEach((courier) => {
      map.set(courier.id, courier.name);
    });
    return map;
  }, [courierIntegrations]);

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'manifest_number',
        headerName: t('table.manifest_number', 'N Manifiesto'),
        sortable: true,
        minWidth: 150,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          return <span className="text-sm font-medium text-text">{manifest.manifest_number}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('table.status', 'Estado'),
        sortable: true,
        minWidth: 130,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          const statusConfig = getManifestStatusConfig(manifest.status);
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}
            >
              {t(`status.${manifest.status}`, statusConfig.label)}
            </span>
          );
        },
      },
      {
        field: 'courier_integration_id',
        headerName: t('table.courier', 'Courier'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => {
          const courierId = params.data?.courier_integration_id;
          return courierMap.get(courierId) || courierId || '-';
        },
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          const courierName = courierMap.get(manifest.courier_integration_id);
          return <span className="text-sm text-text">{courierName || manifest.courier_integration_id || '-'}</span>;
        },
      },
      {
        field: 'orders_count',
        headerName: t('table.orders_count', 'Ordenes'),
        sortable: true,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          return <span className="text-sm text-text">{manifest.orders_count ?? '-'}</span>;
        },
      },
      {
        field: 'packages_count',
        headerName: t('table.packages_count', 'Paquetes'),
        sortable: true,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          return <span className="text-sm text-text">{manifest.packages_count ?? '-'}</span>;
        },
      },
      {
        field: 'weight_total',
        headerName: t('table.weight_total', 'Peso'),
        sortable: true,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          return (
            <span className="text-sm text-text">{manifest.weight_total ? `${manifest.weight_total} kg` : '-'}</span>
          );
        },
      },
      {
        field: 'created_at',
        headerName: t('table.created_at', 'Creado'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => params.data?.created_at,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          return <span className="text-sm text-text">{formatDateTime(manifest.created_at)}</span>;
        },
      },
      {
        field: 'completed_at',
        headerName: t('table.completed_at', 'Completado'),
        sortable: true,
        minWidth: 160,
        valueGetter: (params) => params.data?.completed_at,
        cellRenderer: (params: any) => {
          const manifest = params.data as Manifest;
          return (
            <span className="text-sm text-text">
              {manifest.completed_at ? formatDateTime(manifest.completed_at) : '-'}
            </span>
          );
        },
      },
    ];

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params: any) => {
        const manifest = params.data as Manifest;

        return (
          <div className="flex items-center gap-1 justify-center w-full">
            {manifest.excel_file_path && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownloadExcel?.(manifest)}
                className="h-8 w-8 p-0"
                title={t('actions.downloadExcel', 'Descargar Excel')}
              >
                <FontAwesomeIcon icon={faFileExcel} className="w-3.5 h-3.5 text-green-600" />
              </Button>
            )}
            {manifest.status === 'GENERADO' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUploadPdf?.(manifest)}
                className="h-8 w-8 p-0"
                title={t('actions.uploadPdf', 'Subir PDF')}
              >
                <FontAwesomeIcon icon={faUpload} className="w-3.5 h-3.5 text-blue-600" />
              </Button>
            )}
            {manifest.status === 'COMPLETADO' && manifest.signed_pdf_file_path && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewPdf?.(manifest)}
                className="h-8 w-8 p-0"
                title={t('actions.viewPdf', 'Ver PDF')}
              >
                <FontAwesomeIcon icon={faFilePdf} className="w-3.5 h-3.5 text-red-600" />
              </Button>
            )}
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, courierMap, onUploadPdf, onDownloadExcel, onViewPdf]);

  const filteredColumnDefs = useMemo(() => {
    const alwaysVisible = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field === 'actions' || field === 'manifest_number';
    });

    const selectableColumns = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions';
    });

    if (!selectedColumns.length) {
      return [...selectableColumns, ...alwaysVisible].reduce<ColDef[]>((acc, column) => {
        const field = column.field || column.headerName || 'unknown';
        if (!acc.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
          acc.push(column);
        }
        return acc;
      }, []);
    }

    const selectedDefs = selectableColumns.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return selectedColumns.includes(field);
    });

    const combined = [...selectedDefs];
    alwaysVisible.forEach((column) => {
      const field = column.field || column.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(column);
      }
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs,
  };
};
